import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography
} from '@mui/material'

import useCsrfToken from '@/hooks/useCsrfToken'

import {
  fetchNotifications,
  markNotificationsAsRead
} from '@/Components/Common/Notifications/actions'
import Notification from '@/Components/Common/Notifications/Notification'
import { Row } from '@/Components/Layout/Row'
import { usePage } from '@/Components/Pages/PageProvider'

const Notifications = () => {
  const csrfToken = useCsrfToken()
  const { notifications, setNotifications } = usePage()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (notifications?.length > 0) return
    setLoading(true)
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      const { notifications: loadedNotifications = [], error } =
        await fetchNotifications(csrfToken)
      if (error) {
        setError(error)
        setLoading(false)
        return
      }
      setNotifications(loadedNotifications)
      setLoading(false)
    }
    fetchData()
  }, [csrfToken])

  const handleReadNotifications = useCallback(async () => {
    const { error } = await markNotificationsAsRead(csrfToken)
    if (error) {
      setError(error)
      return
    }
    setNotifications([])
  }, [])

  const baseSx = {
    height: 400,
    width: 400,
    overflow: 'hidden'
  }

  return (
    <Stack sx={baseSx}>
      <Box
        component="header"
        flex={0}
        sx={{ py: 2.5, px: 4, borderBottom: '1px solid #e0e0e0' }}
      >
        <Row
          justifyContent="space-between"
          alignItems="baseline"
          flexWrap="wrap"
        >
          <Typography variant="h4" sx={{ fontSize: '24px' }}>
            Notifications
          </Typography>
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault()
              handleReadNotifications()
            }}
          >
            Mark all as read
          </Link>
        </Row>
      </Box>
      <Box
        sx={{ flex: 1, overflowY: 'auto', pb: 2, display: 'flex' }}
        id="notifications-list"
      >
        {loading && <CircularProgress />}
        {!loading && notifications.length === 0 && !error && (
          <Box
            flex={1}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Typography color="textSecondary" sx={{ p: 2, minWidth: 200 }}>
              No new notifications.
            </Typography>
          </Box>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && notifications.length > 0 && (
          <Stack divider={<Divider sx={{ m: '0 !important' }} />} flex={1}>
            {notifications?.map((notification, i) => (
              <Notification
                key={`notification-${notification.id ?? i}`}
                {...notification}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  )
}

export default Notifications
